import { useMemo } from 'react';
import {
  type AnnotatedJsonSchema,
  type EntityPresentationAnnotation,
  type Nullable,
} from '@web-config-app/core';
import { useTranslate } from '@web-config-app/core-react';
import type {
  ControlProps,
  EntityFormControlProps,
  Translate,
} from '../../types/controls';
import { getCombinatorProperties } from '../../utils/get-combinator-properties/get-combinator-properties.util';

const getLabelAnnotations = (schema: AnnotatedJsonSchema) =>
  schema['x-entity-label'] ?? {};
const getPresentationAnnotations = (schema: AnnotatedJsonSchema) =>
  schema['x-entity-presentation'] ?? {};

const ENUM_CONTROL_PLACEHOLDER_VALUE = 'enumControlPlaceholder';

/**
 * enumOptions apply to the SelectInputControl and provide an array of GDSSelectOptions in one of
 * two ways:
 *
 * 1. localized labels for each raw value in the schema's `enum`.
 * 2. provided by a data source
 */

const getEnumOptions = (
  enumLabels: EntityPresentationAnnotation['enumLabels'] | undefined,
  schema: AnnotatedJsonSchema,
  propsEnumOptions: ControlProps['enumOptions'],
  translate: Translate,
) => {
  if (propsEnumOptions) return propsEnumOptions;

  return enumLabels && Array.isArray(schema.enum)
    ? Object.entries(enumLabels)
        .filter(([value]) => {
          if (schema.enum?.includes(value)) {
            return true;
          }

          if (value === ENUM_CONTROL_PLACEHOLDER_VALUE) {
            return false;
          }

          /**
           * We have an enumLabels map that contains a value that doesn't exist in the actual
           * schema's enum value
           */
          // eslint-disable-next-line no-console
          console.warn(
            `received enumLabel for value ${value} that does not exist in schema.enum`,
          );
          return false;
        })
        .map(([value, translationKey]) => ({
          label: translate(translationKey),
          value,
        }))
    : schema.enum?.map((item) => ({ label: item, value: item }));
};

export function mapAnnotatedSchemaToProps(
  schema: AnnotatedJsonSchema,
  props: Nullable<ControlProps | undefined>,
  translate: Translate,
  path: string,
): Partial<EntityFormControlProps> {
  const {
    key: labelTranslationKey,
    propertyRef: labelPropertyRef,
    text: labelText,
  } = getLabelAnnotations(schema);

  const label =
    labelText ??
    (labelTranslationKey
      ? translate(labelTranslationKey)
      : translate('MISSING_LABEL_ANNOTATION', { path }));

  const {
    hint,
    banner,
    hidden,
    hideLabel,
    enumLabels,
    fieldGroups,
    fieldOrder,
    arrayAddLabel,
    compactHeading,
  } = getPresentationAnnotations(schema);

  const localized = schema['x-entity-localized'] ?? false;
  const enumOptions = getEnumOptions(
    enumLabels,
    schema,
    props?.enumOptions,
    translate,
  );
  const enumPlaceholder =
    enumOptions &&
    translate(enumLabels?.enumControlPlaceholder ?? 'DEFAULT_ENUM_PLACEHOLDER');

  /**
   * combinatorProperties are created when the schema includes a discriminator property and a `oneOf` array
   * The combinator properties include:
   * - the discriminator property name
   * - the options for the select input used for selecting a subschema option
   * - the sub-schemas to be rendered upon option selection
   * - a placeholder for the select input
   * - properties to be added to the primitive control form field wrapping the combinator select input
   */

  const combinatorProperties = getCombinatorProperties(schema, translate);

  /**
   * When virtually grouping properties in an Object control, this returns
   * an array of field groups with a localized label for the fieldset's legend
   */
  const fieldGroupsWithLegends = fieldGroups?.map(
    ({ labelKey = {}, ...fieldGroup }) => {
      const legend =
        labelKey.text ?? (translate(labelKey.key, labelKey.values) as string);
      return {
        ...fieldGroup,
        legend,
      };
    },
  );

  const controlType = schema['x-entity-control'];

  const flattenLayoutType =
    controlType?.type === 'flattenLayout'
      ? controlType?.options?.flattenLayout?.type
      : undefined;

  return {
    banner: banner && {
      ...banner,
      title:
        banner.title?.text ??
        translate(banner.title?.key, banner.title?.values),
      description:
        banner.description?.text ??
        translate(banner.description?.key, banner.description?.values),
    },
    enumOptions,
    enumPlaceholder,
    fieldGroups: fieldGroupsWithLegends,
    fieldOrder,
    label,
    hideLabel: Boolean(hideLabel || flattenLayoutType === 'omitWrapper'),
    compactHeading: Boolean(compactHeading), // if undefined, coerce to false
    hidden: Boolean(hidden), // if undefined, coerce to false
    hint: hint?.text ?? translate(hint?.key, hint?.values),
    arrayAddLabel: translate(arrayAddLabel),
    labelPropertyRef,
    localized,
    combinatorProperties,
  };
}

export type UseAnnotatedSchemaProps = Partial<EntityFormControlProps>;

export type AnnotatedSchemaProps = ReturnType<typeof useAnnotatedSchemaProps>;

export const useAnnotatedSchemaProps = (
  schema: AnnotatedJsonSchema,
  props?: Nullable<ControlProps>,
  path: string = '',
) => {
  const translate = useTranslate();
  return useMemo(
    () => mapAnnotatedSchemaToProps(schema, props, translate, path),
    [schema, props, translate, path],
  );
};
